import React, { useState } from 'react';
import './MultipleImageUpload.css';
import {
  UseFormRegister,
  FieldValues,
  Path,
  UseFormSetError,
} from 'react-hook-form';
import { TrashButton } from '../TrashButton';

interface MultipleImageUploadProps<T extends FieldValues> {
  register: UseFormRegister<T>;
  setValue: (name: Path<T>, value: File[]) => void;
  setError: UseFormSetError<T>;
  name: Path<T>;
  label: string;
}

const MultipleImageUpload = <T extends FieldValues>({
  register,
  setValue,
  setError,
  name,
  label,
}: MultipleImageUploadProps<T>) => {
  const [previews, setPreviews] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setLocalError] = useState<string | null>(null);

  const validateFiles = (selectedFiles: File[]) => {
    const validFormats = ['image/jpeg', 'image/png', 'image/webp'];
    let isValid = true;
    const totalFiles = files.length + selectedFiles.length;
    if (totalFiles > 3) {
      setLocalError('Adicione até três fotos.');
      setError(name, {
        type: 'manual',
        message: 'Adicione até três fotos.',
      });
      isValid = false;
      return isValid;
    }

    selectedFiles.forEach((file) => {
      if (!validFormats.includes(file.type)) {
        setLocalError('Formato inválido. Apenas JPG, PNG ou WEBP são aceitos.');
        setError(name, {
          type: 'manual',
          message: 'Formato inválido. Apenas JPG, PNG ou WEBP são aceitos.',
        });
        isValid = false;
      }
      if (file.size > 10 * 1024 * 1024) {
        setLocalError('Tamanho máximo 10MB por imagem.');
        setError(name, {
          type: 'manual',
          message: 'Tamanho máximo 10MB por imagem.',
        });
        isValid = false;
      }
    });

    if (isValid) {
      setLocalError(null);
      setError(name, { type: 'manual', message: '' });
    }

    return isValid;
  };

  const handleImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
      ? Array.from(event.target.files)
      : [];

    if (validateFiles(selectedFiles)) {
      const newPreviews = selectedFiles.map((file) =>
        URL.createObjectURL(file)
      );

      setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
      setPreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);
      setValue(name, [...files, ...selectedFiles]);
    }
  };

  const handleRemoveImage = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);

    setFiles(newFiles);
    setPreviews(newPreviews);
    setValue(name, newFiles);
  };

  return (
    <>
      <label
        className="label-form-inputs"
        dangerouslySetInnerHTML={{ __html: label }}
      />
      <div className="upload-wrapper">
        {previews.map((preview, index) => (
          <div key={index} className="image-upload-item">
            <img
              src={preview}
              alt={`Preview ${index}`}
              className="image-upload-preview"
            />
            <TrashButton onClick={() => handleRemoveImage(index)}></TrashButton>
          </div>
        ))}
        {files.length < 3 && (
          <label className={` upload-label ${error ? 'has-error' : ''}`}>
            <input
              type="file"
              accept="image/*"
              multiple
              {...register(name, { required: true })}
              onChange={handleImagesChange}
              className="upload-input"
            />
            <div className="upload-placeholder">+</div>
          </label>
        )}
        {error && <span className="error-message">{error}</span>}
      </div>
    </>
  );
};

export default MultipleImageUpload;
